/**
 * Vérifications: a pro's national ID card or passport, checked (WALLIYO-0035).
 *
 * Verified means one thing, the same for every trade: an ID card or passport
 * that staff (or, later, a government or third-party check) confirmed belongs
 * to this pro. Nobody unverified can be found or hired (WALLIYO-0065).
 *
 * Two sides:
 * - the pro submits from the app: `POST /api/v1/verifications`;
 * - staff review in the console: `/api/v1/console/verifications…`, every
 *   response under its own name (WALLIYO-0068), every write through
 *   performAction with a reason, an `Idempotency-Key` and a `version`.
 *
 * What is never stored or shown: the document number itself. The server keeps
 * an HMAC of it (to recognise a banned document) and its last 4 characters (so
 * staff can check it against the image).
 */

// ── Shared words ──────────────────────────────────────────────────────────

export const ID_DOCUMENT_TYPES = ["national_id", "passport"] as const;
export type IdDocumentType = (typeof ID_DOCUMENT_TYPES)[number];

/**
 * - `submitted`: waiting for staff.
 * - `flagged`: waiting for staff, and the document number matches a banned
 *   document. It can never be approved. The pro is not told.
 * - `approved`, `rejected`: decided. Final: a rejected pro submits again.
 */
export const VERIFICATION_STATUSES = ["submitted", "flagged", "approved", "rejected"] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

/** The images a submission carries. A passport has no `back`. */
export const ID_IMAGE_SIDES = ["front", "back", "selfie"] as const;
export type IdImageSide = (typeof ID_IMAGE_SIDES)[number];

/** Image types the server accepts, checked by their first bytes, not their name. */
export const ID_IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type IdImageContentType = (typeof ID_IMAGE_CONTENT_TYPES)[number];

/**
 * The most one image may weigh. The app compresses before sending (the pro
 * pays for data); this is the ceiling, not the target.
 */
export const ID_IMAGE_MAX_BYTES = 1_500_000;

/** How long a URL from `GET …/images` works. Short: it is a key to an ID. */
export const ID_IMAGE_URL_TTL_SECONDS = 120;

/** A document number, once spaces, dashes and dots are removed: 5 to 20 letters or digits. */
export const DOCUMENT_NUMBER_PATTERN = /^[A-Z0-9]{5,20}$/;

/**
 * Why staff rejected an ID. The pro is told in the app, in their own language,
 * from the text for this code (shared/src/notifications/verification-messages.ts).
 * A document matching a banned one is always told `document_not_accepted`,
 * whatever staff picked: the pro is never told it is banned.
 */
export const VERIFICATION_REJECTION_REASON_CODES = [
  "photo_unreadable",
  "selfie_mismatch",
  "document_expired",
  "document_not_accepted",
  "other",
] as const;
export type VerificationRejectionReasonCode = (typeof VERIFICATION_REJECTION_REASON_CODES)[number];

/** The most characters of a rejection's staff note. Internal: the pro never sees it. */
export const REJECTION_NOTE_MAX_LENGTH = 300;

// ── The pro submits (the app) ─────────────────────────────────────────────

/**
 * POST /api/v1/verifications
 *
 * Customer auth (`Authorization: Bearer …`), and the caller must have a pro
 * profile. Needs SUBMIT_VERIFICATION_USER_FEATURE_ENABLEMENT on for them.
 *
 * Sent as `multipart/form-data`, one request, so a flaky connection never
 * leaves half a submission:
 *
 * | part             | kind | required                          |
 * | ---------------- | ---- | --------------------------------- |
 * | `documentType`   | text | yes: `national_id` or `passport`  |
 * | `documentNumber` | text | yes                               |
 * | `country`        | text | no, ISO 3166-1 alpha-2; `GN`      |
 * | `front`          | file | yes                               |
 * | `back`           | file | for `national_id` only            |
 * | `selfie`         | file | yes                               |
 *
 * Each file is a JPEG, PNG or WebP of at most ID_IMAGE_MAX_BYTES. Send an
 * `Idempotency-Key` header generated once per tap on "Envoyer": a retry after
 * a timeout returns the first answer instead of a conflict.
 *
 * Refused with `conflict` when the pro is already verified or already has a
 * submission waiting.
 */
export type SubmitVerificationRequest = {
  documentType: IdDocumentType;
  documentNumber: string;
  country?: string;
};

export type SubmitVerificationResponse = {
  verification: {
    identityVerificationId: string;
    /** Always `in_review` to the pro, including when staff see it as flagged. */
    status: "in_review";
    documentType: IdDocumentType;
    documentNumberLast4: string;
    submittedAt: string;
  };
};

// ── Staff review (the console) ────────────────────────────────────────────

/** Rows per page when `limit` is left out, and the most one page may hold. */
export const VERIFICATIONS_DEFAULT_LIMIT = 50;
export const VERIFICATIONS_MAX_LIMIT = 200;

/**
 * GET /api/v1/console/verifications
 *
 * The queue, oldest first. Needs REVIEW_VERIFICATIONS. Query-string filters,
 * combined with AND:
 * - `status`: one status, or `open` (the default: `submitted` and `flagged`);
 * - `documentType`, `country`;
 * - `cursor`, `limit` to page.
 */
export type ListVerificationsRequest = {
  status?: VerificationStatus | "open";
  documentType?: IdDocumentType;
  country?: string;
  cursor?: string;
  limit?: number;
};

export type VerificationPro = {
  userId: string;
  workerId: string;
  name: string;
  /** e.g. "+224 6•• •• •• 41". Null when the account has no phone. */
  maskedPhone: string | null;
  category: string;
  city: string;
};

export type VerificationListItem = {
  identityVerificationId: string;
  status: VerificationStatus;
  documentType: IdDocumentType;
  country: string;
  documentNumberLast4: string;
  pro: VerificationPro;
  /** True when the number matches a banned document now, whatever the status. */
  matchesBannedDocument: boolean;
  submittedAt: string;
  version: number;
};

export type ListVerificationsResponse = {
  verifications: readonly VerificationListItem[];
  /** Pass as `cursor` for the next page. `null` on the last page. */
  nextCursor: string | null;
};

/**
 * What an automatic check found. Manual review finds nothing, so every field
 * is null and `result` is `needs_review`; a government or third-party check
 * fills them in without the screen changing.
 */
export type VerificationCheck = {
  method: "manual_review";
  result: "needs_review" | "passed" | "failed";
  extracted: {
    fullName: string | null;
    dateOfBirth: string | null;
    expiresOn: string | null;
  };
};

/**
 * GET /api/v1/console/verifications/:identityVerificationId
 *
 * Needs REVIEW_VERIFICATIONS. No images: they need VIEW_ID_IMAGES and their
 * own audited call.
 */
export type GetVerificationResponse = {
  verification: VerificationListItem & {
    check: VerificationCheck;
    /** Set when the number matches a banned document. */
    bannedDocument: { reason: string; bannedAt: string } | null;
    /** Which images exist, and whether retention has deleted them. */
    images: { sides: readonly IdImageSide[]; deletedAt: string | null };
    /** Why it was rejected, as staff picked it. Null unless rejected. */
    rejectionReasonCode: VerificationRejectionReasonCode | null;
    /** The staff note on a rejection: internal, never shown to the pro. */
    rejectionReason: string | null;
    reviewedBy: { staffMemberId: string; name: string } | null;
    reviewedAt: string | null;
    /** What the pro's account says now: the fields approval keeps in step. */
    proStanding: {
      verificationStatus: "unverified" | "pending" | "verified" | "rejected";
      approvalStatus: "pending" | "approved" | "rejected";
      isVerified: boolean;
    };
    /** Their earlier submissions, newest first. */
    previousAttempts: readonly { identityVerificationId: string; status: VerificationStatus; submittedAt: string }[];
  };
};

/**
 * GET /api/v1/console/verifications/:identityVerificationId/images
 *
 * Needs VIEW_ID_IMAGES. Always audited (VIEW_ID_IMAGES_STAFF_ACTION, a
 * sensitive read) with the pro as its subject, so their history shows who
 * looked. The URLs work for ID_IMAGE_URL_TTL_SECONDS; ask again after.
 * Refused with `validation_error` once retention has deleted the images.
 */
export type GetVerificationImagesResponse = {
  images: readonly { side: IdImageSide; signedUrl: string }[];
  expiresAt: string;
};

/**
 * POST /api/v1/console/verifications/:identityVerificationId/approve
 *
 * Needs REVIEW_VERIFICATIONS and APPROVE_PROS. In one transaction: the
 * verification becomes `approved`, and the pro's `is_verified`,
 * `approval_status` and `verification_status` follow, so they can be found
 * and hired. Refused when the number matches a banned document.
 */
export type ApproveVerificationRequest = { reason: string; version: number };

/**
 * POST /api/v1/console/verifications/:identityVerificationId/reject
 *
 * Needs REVIEW_VERIFICATIONS. Staff pick a `reasonCode`; the pro gets an
 * in-app notification saying why, in their own language, in the same
 * transaction as the rejection (never an SMS). The `note` is for the team:
 * it is recorded with the decision and never shown to the pro.
 */
export type RejectVerificationRequest = {
  reasonCode: VerificationRejectionReasonCode;
  note?: string | null;
  version: number;
};

export type DecideVerificationResponse = {
  verification: VerificationListItem;
};

export type RejectVerificationResponse = DecideVerificationResponse;
